/**
 * Wire contract for the learner communication-preference endpoints.
 *
 * Kept local because the committed generated client does not yet contain
 * `backend/app/dtos/communication_preferences/communication_preference.py` or
 * `channel_state.py`. These unions mirror the ENG-3630 and ENG-3631 contracts;
 * snake_case is intentional because these values cross the API boundary.
 */
export type NotificationChannel = 'slack' | 'email' | 'in_app' | 'hubspot' | 'sms'

export type NotificationPurpose =
  | 'meeting_reminder'
  | 'coaching_nudge'
  | 'learning_nudge'
  | 'program_invite'
  | 'progress'
  | 'research'
  | 'coach_direct'
  | 'meeting_change'
  | 'enrollment_critical'
  | 'account'
  | 'internal'

export type LearnerVisibleNotificationPurpose = Exclude<NotificationPurpose, 'internal'>
export type PreferenceChannel = Extract<NotificationChannel, 'email' | 'sms' | 'in_app'>
export type OutboundPreferenceChannel = Extract<PreferenceChannel, 'email' | 'sms'>

export const LEARNER_VISIBLE_NOTIFICATION_PURPOSES = [
  'meeting_reminder',
  'coaching_nudge',
  'learning_nudge',
  'program_invite',
  'progress',
  'research',
  'coach_direct',
  'meeting_change',
  'enrollment_critical',
  'account',
] as const satisfies readonly LearnerVisibleNotificationPurpose[]

export type ChannelBlockedReason = 'no_address' | 'staff_refused' | 'learner_stopped' | 'undeliverable'

export type CommunicationPreferenceEntry = {
  readonly channel: PreferenceChannel
  readonly purpose: LearnerVisibleNotificationPurpose
  readonly enabled: boolean
  readonly declinable: boolean
}

export type NotificationPreferenceChannelState = {
  readonly channel: OutboundPreferenceChannel
  readonly usable: boolean
  readonly blocked_reason: ChannelBlockedReason | null
  readonly address: string | null
  readonly start_keyword_number: string | null
}

export type CommunicationPreferences = {
  readonly preferences: readonly CommunicationPreferenceEntry[]
  readonly channels: readonly NotificationPreferenceChannelState[]
}

export type CommunicationPreferenceUpdate = Pick<CommunicationPreferenceEntry, 'channel' | 'purpose' | 'enabled'>

export type CommunicationPreferencesUpdateRequest = {
  readonly preferences: readonly CommunicationPreferenceUpdate[]
}

const VISIBLE_PURPOSES = new Set<string>(LEARNER_VISIBLE_NOTIFICATION_PURPOSES)
const PREFERENCE_CHANNELS = new Set<string>(['email', 'sms', 'in_app'])
const OUTBOUND_CHANNELS = new Set<string>(['email', 'sms'])
const BLOCKED_REASONS = new Set<string>(['no_address', 'staff_refused', 'learner_stopped', 'undeliverable'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Runtime boundary for the focused notification-preference wire contract. */
export function parseCommunicationPreferences(value: unknown): CommunicationPreferences | null {
  if (!isRecord(value) || !Array.isArray(value.preferences) || !Array.isArray(value.channels)) return null
  if (value.preferences.length !== LEARNER_VISIBLE_NOTIFICATION_PURPOSES.length * PREFERENCE_CHANNELS.size) return null

  const preferences: CommunicationPreferenceEntry[] = []
  const preferenceKeys = new Set<string>()
  for (const candidate of value.preferences) {
    if (
      !isRecord(candidate) ||
      typeof candidate.channel !== 'string' ||
      !PREFERENCE_CHANNELS.has(candidate.channel) ||
      typeof candidate.purpose !== 'string' ||
      !VISIBLE_PURPOSES.has(candidate.purpose) ||
      typeof candidate.enabled !== 'boolean' ||
      typeof candidate.declinable !== 'boolean'
    ) {
      return null
    }
    const key = `${candidate.channel}:${candidate.purpose}`
    if (preferenceKeys.has(key)) return null
    preferenceKeys.add(key)
    preferences.push(candidate as CommunicationPreferenceEntry)
  }

  for (const purpose of LEARNER_VISIBLE_NOTIFICATION_PURPOSES) {
    for (const channel of PREFERENCE_CHANNELS) {
      if (!preferenceKeys.has(`${channel}:${purpose}`)) return null
    }
  }

  if (value.channels.length !== OUTBOUND_CHANNELS.size) return null
  const channels: NotificationPreferenceChannelState[] = []
  const channelKeys = new Set<string>()
  for (const candidate of value.channels) {
    if (
      !isRecord(candidate) ||
      typeof candidate.channel !== 'string' ||
      !OUTBOUND_CHANNELS.has(candidate.channel) ||
      typeof candidate.usable !== 'boolean' ||
      (candidate.blocked_reason !== null &&
        (typeof candidate.blocked_reason !== 'string' || !BLOCKED_REASONS.has(candidate.blocked_reason))) ||
      (candidate.address !== null && typeof candidate.address !== 'string') ||
      (candidate.start_keyword_number !== null && typeof candidate.start_keyword_number !== 'string')
    ) {
      return null
    }
    if (channelKeys.has(candidate.channel)) return null
    channelKeys.add(candidate.channel)
    channels.push(candidate as NotificationPreferenceChannelState)
  }

  if (![...OUTBOUND_CHANNELS].every((channel) => channelKeys.has(channel))) return null
  return { preferences, channels }
}

export function preferenceKey(
  channel: PreferenceChannel,
  purpose: LearnerVisibleNotificationPurpose
): `${PreferenceChannel}:${LearnerVisibleNotificationPurpose}` {
  return `${channel}:${purpose}`
}
